import React, { useEffect } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { RescueContextProvider, useRescueContext } from './lib/RescueContext';
import { ToastProvider } from './components/Toast/ToastContext';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import Dashboard from './pages/Dashboard/Dashboard';
import Chips from './pages/Chips/Chips';
import Devices from './pages/Devices/Devices';
import Gwa from './pages/Gwa/Gwa';
import Integrations from './pages/Integrations/Integrations';
import HelpCenter from './pages/HelpCenter/HelpCenter';
import { trackRescuePageView } from './analytics';
import styles from './RescuePortal.module.scss';

const browserTitles: Record<string, string> = {
  '/rescue': 'Dashboard',
  '/rescue/chips': 'Fi Nano Microchips',
  '/rescue/devices': 'Fi Devices',
  '/rescue/gwa': 'Gift with Adoption',
  '/rescue/integrations': 'Integrations',
  '/rescue/help': 'Help Center',
};

function PortalLayout() {
  const location = useLocation();
  const { sidebarOpen, setSidebarOpen } = useRescueContext();

  // Set browser tab title + track page view
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, '') || '/rescue';
    const title = browserTitles[path] || 'Dashboard';
    document.title = `${title} — Fi for Rescues`;
    trackRescuePageView(path, title);
  }, [location.pathname]);

  return (
    <Switch>
      {/* Help Center is a standalone page — no sidebar, but includes header for nav + impersonation banner */}
      <Route path="/rescue/help">
        <div className={`${styles.portalBodyStandalone} ${styles.fadeIn}`}>
          <Header />
          <HelpCenter />
        </div>
      </Route>

      {/* All other pages use the full portal shell */}
      <Route>
        <div className={styles.portalBody}>
          <Sidebar />
          <div
            className={`${styles.sidebarOverlay} ${sidebarOpen ? styles.overlayOpen : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
          <main className={styles.portalMain}>
            <Header />
            <div key={location.pathname} className={`${styles.portalContent} ${styles.fadeIn}`}>
              <Switch>
                <Route exact path="/rescue" component={Dashboard} />
                <Route path="/rescue/chips" component={Chips} />
                <Route path="/rescue/devices" component={Devices} />
                <Route path="/rescue/gwa" component={Gwa} />
                <Route path="/rescue/integrations" component={Integrations} />
              </Switch>
            </div>
          </main>
        </div>
      </Route>
    </Switch>
  );
}

export default function RescuePortal() {
  return (
    <RescueContextProvider>
      <ToastProvider>
        <PortalLayout />
      </ToastProvider>
    </RescueContextProvider>
  );
}
